import React, { createContext, useState } from "react";
import Axios from "axios";
import { message } from "antd";
import config from "../config";

export const SignUpContext = createContext({});

// ---------- localStorage safe helpers ----------
const canUseLocalStorage = () => {
  try {
    const k = "__ls_test__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
};

const safeGet = (key, fallback = null) => {
  try {
    if (!canUseLocalStorage()) return fallback;
    const v = window.localStorage.getItem(key);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

const safeSet = (key, value) => {
  try {
    if (!canUseLocalStorage()) return;
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const getAccessToken = () => safeGet("access_token", "");

export const SignUpProvider = (props) => {
  const [profileData, setProfileData] = useState({
    categories: [],
    membership: { name: "Sponsor", cost: 0 },
    social: { facebook: "", instagram: "", twitter: "", youtube: "" },
    referral: { firstname: "", middlename: "", lastname: "" },
    adImage: "",
    bannerStartDate: "",
    bannerData: { banner: false, adCost: 0 },
    skipBanner: false,
    page: "",
    banner: false,
    location: { formatted_address: "" },
    termAgreement: false,
    term2Agreement: false,
    cartTotal: 0,
  });

  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [disableNext, setDisableNext] = useState(true);

  // ✅ safe localStorage read + ensure number
  const [step, setStep] = useState(() => {
    const s = safeGet("step", "1");
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  const saveStepData = async () => {
    setLoading(true);

    const doc = { onBoarding: step };

    try {
      await Axios.patch(
        `${config.USERS}/users`,
        {},
        {
          headers: {
            token: getAccessToken(),
            doc: encodeURIComponent(JSON.stringify(doc)),
          },
        }
      );

      setStep((prev) => {
        const next = prev + 1;
        safeSet("step", String(next));
        return next;
      });

      setLoading(false);
    } catch (err) {
      message.error("Something went wrong please try again");
      setLoading(false);
    }
  };

  const saveService = async () => {
    const service = parseServiceFromState();

    try {
      setLoading(true);

      await Axios.patch(
        `${config.USERS}/users`,
        {},
        {
          headers: {
            token: getAccessToken(),
            doc: encodeURIComponent(
              JSON.stringify({
                advertiserProfile: service,
              })
            ),
          },
        }
      );

      message.success("Your profile has been saved!");
      setLoading(false);
      saveStepData();
    } catch (error) {
      message.error(
        "Something went wrong, if you continue to get this message please contact support"
      );
      console.log("Error - ", error);
      setLoading(false);
    }
  };

  const saveCompletedProfile = async () => {
    setLoading(true);

    const doc = { onBoarding: profileData };

    try {
      const response = await Axios.patch(
        `${config.USERS}/users`,
        {},
        {
          headers: {
            token: getAccessToken(),
            doc: encodeURIComponent(JSON.stringify(doc)),
          },
        }
      );

      if (response.status === 200) {
        setLoading(false);
        safeSet("onboard", JSON.stringify(profileData));
        safeSet("step", "1");
        setStep(1);
      } else {
        message.error("Something went wrong please try again");
        setLoading(false);
      }
    } catch (e) {
      message.error("Something went wrong please try again");
      setLoading(false);
    }
  };

  const parseServiceFromState = () => {
    const user_id = safeGet("userid", "");

    const {
      organization_name,
      coordinates,
      places_component,
      categories,
      amenities,
      description,
      licenses,
      referral,
      website,
      storeHours,
      logoImageUrl,
      bannerImageUrl,
      social,
      formatted_address,
    } = profileData;

    const emails = {
      primary: profileData.primaryEmail,
      alternativeEmail: profileData.alternativeEmail,
    };

    const contact = {
      emails,
      phone: profileData.phone,
      primaryPhone: profileData.primaryPhone,
    };

    const location = [
      {
        contact,
        tags: [],
        places_component,
        geo: { type: "Point", coordinates },
        hours: storeHours,
        formatted_address,
      },
    ];

    const images = { logo: logoImageUrl, profile: bannerImageUrl };

    const descriptors = {
      website,
      categories,
      amenities,
      description,
      social,
    };

    return {
      referral,
      contact,
      user_id,
      images,
      descriptors,
      organization_name,
      location,
      licenses,
    };
  };

  const getInitialData = async () => {
    const { data } = await Axios.get(`${config.USERS}/users`, {
      cors: true,
      responseType: "json",
      headers: { token: getAccessToken() },
    });

    if (data?.advertiserProfile) {
      const { descriptors, contact, images } = data.advertiserProfile;

      setProfileData((prev) => ({
        ...prev,
        organization_name: data.advertiserProfile?.organization_name || "",
        bannerImageUrl: images?.profile || "",
        logoImageUrl: images?.logo || "",
        categories: descriptors?.categories || [],
        primaryEmail: contact?.emails?.primary || "",
        primaryPhone: contact?.primaryPhone || "",
        alternativeEmail: contact?.emails?.alternativeEmail || "",
        phone: contact?.phone || "",
        website: descriptors?.website || "",
        description: descriptors?.description || "",
        referral: data?.advertiserProfile?.referral || "",
        social: descriptors?.social || { facebook: "", instagram: "", twitter: "", youtube: "" },
        formatted_address: data?.advertiserProfile?.location?.[0]?.formatted_address || "",
      }));
    }
  };

  const data = {
    isLoading,
    setLoading,
    getInitialData,
    profileData,
    setProfileData,
    step,
    setStep,
    disableNext,
    setDisableNext,
    saveService,
    saveStepData,
    showCompletedModal,
    setShowCompletedModal,
    saveCompletedProfile,
  };

  return (
    <SignUpContext.Provider value={data}>
      {props.children}
    </SignUpContext.Provider>
  );
};

export default SignUpProvider;
